// packages/core/src/learning/school-modules/money-psychology-housel.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'money-psychology-housel' school. LearningModule,
// surface 'school'.
//   module id   = 'school-money-psychology-housel' (matches exhibit nativeModuleId)
//   homeSchool  = 'money-psychology-housel'
// PROMOTED: five authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_MONEY_PSYCHOLOGY_HOUSEL_MODULE: LearningModule = {
	id: 'school-money-psychology-housel',
	title: 'Money Psychology (Housel)',
	description:
		'The lens that puts behavior before math — luck and risk, the skill of enough, time as the quiet force, and where insight has to turn into action.',
	emoji: '🪙',
	accentColor: '#8e6fc4',
	tier: 'growth',
	pillar: 'money-psychology-core',
	order: 0,
	surface: 'school',
	homeSchool: 'money-psychology-housel',
	lessons: [
		{
			id: 'school-money-psychology-housel-01',
			title: 'Money is behavior before math',
			description:
				'This lesson teaches why money psychology cares less about perfect formulas and more about what you can repeat under pressure.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['money-psychology-housel'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: [
				'money-psychology-housel',
				'behavior-over-math',
				'temperament',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'behavior beats formulas',
				'lived experience shapes money',
				'numbers need temperament',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'You can understand interest, savings, investing, and debt, then still make choices that confuse you later. Money psychology notices that the hard part is often not knowing the number. The hard part is staying steady when the number touches fear, status, family, regret, or pressure.',
				},
				{
					type: 'paragraph',
					text: 'Money psychology believes money is behavior before math. The spreadsheet matters, but the person using the spreadsheet matters more. A plan only counts if a real human can live with it through boredom, surprise bills, bad timing, and comparison.',
				},
				{
					type: 'bullet_list',
					items: [
						'Your past shapes what feels risky',
						'Your family shapes what feels normal',
						'Your stress level shapes what feels urgent',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is assuming the smartest plan is automatically the plan you will follow.',
				},
				{
					type: 'example',
					text: 'Someone might know that saving $300 a month helps future freedom, but if their past taught them that money disappears quickly, they may feel pressure to spend while they can. The math is simple. The behavior is not.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can become soft if it never faces the numbers. Feelings matter, but feelings alone can also justify avoidance, panic, or delay. Money psychology works best when it explains behavior and then tests that behavior against reality.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens connects best to your Dashboard. Look at the gap between what your numbers say and what your behavior does when life gets loud. That gap is not a reason to feel broken. It is the exact place where better judgment gets built.',
				},
				{
					type: 'key_takeaway',
					text: 'A money plan is not real until your behavior can carry it.',
				},
			],
			audioScript:
				'Money psychology starts with a blunt idea: most money problems are not just math problems. That does not mean math is useless. Math matters a lot. Interest matters. Savings rate matters. Time matters. Risk matters. But the numbers do not live by themselves. They live inside a person. That person has memories, pressure, pride, fear, family expectations, past mistakes, and a nervous system that reacts when money feels unsafe.\n\nThink about two people who both have $500 left over at the end of the month. On paper, they are in the same position. But one person grew up seeing money as stable. Bills were paid. Emergencies were handled. Saving feels normal. The other person grew up watching money disappear. Maybe rent was late. Maybe parents fought about bills. Maybe every good month was followed by a bad one. For that person, the same $500 may not feel like calm opportunity. It may feel temporary. It may feel like, I better enjoy this before something takes it.\n\nThat is what this lens notices. Money decisions are not made by robots. They are made by people trying to feel safe, respected, free, prepared, or in control. This is why a plan can be mathematically correct and still fail in real life. If the plan ignores your stress, your habits, your attention, and your environment, it might look smart while being impossible for you to repeat.\n\nMoney psychology also protects you from judging people too quickly. Someone may carry credit card debt, overspend after payday, avoid checking their account, or chase status. It is easy to call that irresponsible. Sometimes discipline really is part of the issue. But the better question is, what belief is driving the behavior? Are they trying to feel secure? Are they trying to prove they are not poor anymore? Are they avoiding shame? Are they copying the people around them? Once you see the belief, the behavior makes more sense.\n\nThe tradeoff is that psychology can become an excuse if it never touches real numbers. You can understand your feelings forever and still not change the pattern. Nisk should not turn this into vague motivation. The point is to connect the behavior to the number. Your Dashboard shows where you are. Your behavior shows what you repeat. The work is to close that gap without shame.\n\nSo the takeaway is simple: the spreadsheet is not the whole plan. The plan is the spreadsheet plus the person who has to live it. If you want a money system that lasts, do not only ask, does this make sense? Ask, can I still do this when I am tired, tempted, scared, or bored? That is where money psychology becomes useful.',
			flashcards: [
				{
					id: 'school-money-psychology-housel-01-fc-1',
					role: 'main_idea',
					front: 'What does money psychology notice?',
					back: 'It notices that behavior, fear, status, and past experience shape money choices.',
				},
				{
					id: 'school-money-psychology-housel-01-fc-2',
					role: 'watch_out',
					front: 'What is the spreadsheet trap?',
					back: 'Thinking a plan is good just because the math works.',
				},
				{
					id: 'school-money-psychology-housel-01-fc-3',
					role: 'use_it',
					front: 'How do you use this lens?',
					back: 'Compare your real behavior against your real numbers without shame.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Compare behavior to your numbers',
			},
			revision: 1,
		},
		{
			id: 'school-money-psychology-housel-02',
			title: 'Luck and risk share the room',
			description:
				'This lesson teaches why money psychology treats success and failure with more humility than ego.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['money-psychology-housel'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['money-psychology-housel', 'luck-and-risk', 'humility'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['luck and risk', 'humble outcomes', 'control illusion'],
			blocks: [
				{
					type: 'paragraph',
					text: 'People love clean money stories. Someone succeeds, so they must be brilliant. Someone struggles, so they must have made bad choices. Money psychology pushes back. Real life is messier than that.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes luck and risk are always in the room. Good decisions can have bad outcomes. Bad decisions can get rewarded for a while. Skill matters, but timing, health, family support, market cycles, layoffs, and random breaks matter too.',
				},
				{
					type: 'bullet_list',
					items: [
						'A good outcome is not always pure skill',
						'A bad outcome is not always pure failure',
						'A smart plan still needs room for surprise',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is confusing the result with the quality of the decision.',
				},
				{
					type: 'example',
					text: 'Someone invests steadily for ten years during a strong market and looks disciplined. Another person starts right before a downturn and feels foolish. The first person may have done well, and the second may have made a reasonable choice with worse timing.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that humility can slide into helplessness if you take it too far. Luck matters, but that does not mean choices are meaningless. Risk matters, but that does not mean planning is pointless.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, test this by running a rougher scenario in Simulate. Ask whether your plan only works in a clean version of life, or whether it still has room for delay, surprise costs, lower returns, or a slower start.',
				},
				{
					type: 'key_takeaway',
					text: 'Judge the decision, not just the outcome.',
				},
			],
			audioScript:
				'Money psychology has a problem with clean stories. Clean stories are tempting because they make life feel easy to explain. Someone built wealth, so they must be disciplined, smart, and superior. Someone fell behind, so they must have been careless, lazy, or bad with money. That kind of thinking feels simple, but it is not honest enough.\n\nThis lens says luck and risk share the room. They show up in almost every money outcome. Skill matters. Work matters. Patience matters. Spending habits matter. But so do things you did not fully control. The family you were born into. The year you graduated. The job market when you needed work. Whether you had help during an emergency. Whether your body stayed healthy. Whether the market was kind or brutal during your first years of investing. Whether one mistake happened during a forgiving season or a punishing one.\n\nThis does not erase responsibility. That is important. Money psychology is not saying choices do not matter. It is saying outcomes are not perfect report cards. A person can make a thoughtful decision and still get hit by bad timing. Another person can make a reckless decision and get rewarded for a while. If you only look at the result, you may learn the wrong lesson.\n\nImagine two people who both start investing. One begins during a long rising market. Their confidence grows fast. They may think they are unusually smart or unusually calm. Another person starts before a downturn. They do the same basic behavior, but their account drops early. They may think they are bad at this or that investing was a mistake. The behavior might be similar. The timing was different. That is luck and risk.\n\nThis lens protects you from ego when things go well and shame when things go badly. When you win, it asks you to stay humble. Maybe you made good choices. Also, maybe conditions helped. When you lose, it asks you to look carefully instead of collapsing into self blame. Maybe you made a mistake. Also, maybe risk showed up.\n\nThe tradeoff is that this idea can be misused. Some people hear, luck matters, and turn that into, nothing is in my control. That is not the point. The point is to control what you can while respecting what you cannot. You can control your savings habits more than market timing. You can control whether your plan has room for error. You can control whether one bad month destroys your whole system. You can control whether you confuse a lucky win with a repeatable skill.\n\nInside Nisk, this lesson belongs in Simulate. Run a version of your plan where life is not perfect. Lower the surplus. Delay the timeline. Add a surprise. The question is not, can my plan work in a clean story? The better question is, can it survive a normal messy life? That is what luck and risk teach.',
			flashcards: [
				{
					id: 'school-money-psychology-housel-02-fc-1',
					role: 'main_idea',
					front: 'Why do luck and risk matter?',
					back: 'They remind you that outcomes are never pure skill or pure failure.',
				},
				{
					id: 'school-money-psychology-housel-02-fc-2',
					role: 'watch_out',
					front: 'What is the outcome trap?',
					back: 'Judging a decision only by how it turned out.',
				},
				{
					id: 'school-money-psychology-housel-02-fc-3',
					role: 'use_it',
					front: 'How do you apply humility?',
					back: 'Test whether your plan still works when life is messier.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate a rougher path',
			},
			revision: 1,
		},
		{
			id: 'school-money-psychology-housel-03',
			title: 'Enough is a real money skill',
			description:
				'This lesson teaches why money psychology treats enough as protection against endless comparison.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['money-psychology-housel'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['money-psychology-housel', 'enough', 'status-pressure'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['enough point', 'status pressure', 'stopping skill'],
			blocks: [
				{
					type: 'paragraph',
					text: 'More money can create more options, but more can also become a treadmill. The target moves. The lifestyle grows. The comparison gets sharper. Money psychology notices that without enough, progress can still feel empty.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes enough is not laziness. Enough is a stopping skill. It is the ability to say, this goal matters, but I will not sacrifice every part of my life to impress people or chase a number that never stops moving.',
				},
				{
					type: 'bullet_list',
					items: [
						'Enough protects your peace',
						'Enough limits status pressure',
						'Enough keeps ambition from eating your life',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking every upgrade is progress.',
				},
				{
					type: 'example',
					text: 'Someone doubles their income but doubles their lifestyle too. Bigger apartment, nicer car, more subscriptions, more pressure to keep up. Their income changed, but their freedom did not change much.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that enough can become avoidance if it hides fear. Some people call it enough when they are actually afraid to try, negotiate, build skills, or face their numbers. Enough should calm ambition, not kill growth.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, this lens becomes useful when you define your enough point before comparison takes over. You can write what enough means for this season, then compare it against your Dashboard and future scenarios.',
				},
				{
					type: 'key_takeaway',
					text: 'Enough is the line that keeps progress from turning into a cage.',
				},
			],
			audioScript:
				'Enough sounds simple until life starts testing it. You think you will know when you have enough money, enough income, enough status, enough comfort, enough proof that you made it. Then the target moves. You make more, and the people around you spend more. You hit one goal, and a bigger goal appears. You get the thing you wanted, and it becomes normal faster than you expected. Money psychology pays close attention to that pattern.\n\nThis lens says enough is a real money skill. It is not laziness. It is not quitting. It is not pretending money does not matter. Money does matter. More money can buy safety, time, choices, better timing, and less fear. But without a stopping point, more can turn into a treadmill. The number goes up, but your peace does not. The income rises, but the pressure rises with it.\n\nEnough is the ability to say, this matters, but not at any cost. I want progress, but I do not want my whole life to become proof that I am successful. I want options, but I do not want to trade every quiet hour, every relationship, every healthy habit, and every bit of attention for a lifestyle I have to keep feeding.\n\nHere is a simple example. Someone starts earning more money. At first, that extra income could create breathing room. It could build runway, reduce stress, or make future choices easier. But then lifestyle pressure shows up. A nicer apartment. A more expensive car. More dinners out. Better clothes. More subscriptions. Better trips. None of these are automatically wrong. The problem is when every raise gets absorbed before it becomes freedom. The person earns more, but their life does not get lighter. It just gets more expensive.\n\nMoney psychology warns against confusing upgrades with progress. An upgrade can be good if it serves your values. It can be a trap if it only serves comparison. Enough helps you separate the two. It asks, what is this buying me? More peace, more time, more health, more connection, more useful joy? Or just proof that I am not behind?\n\nThe blind spot matters too. Enough can become an excuse. Sometimes people say, I do not need more, when the truth is they are scared to grow, scared to negotiate, scared to look at their numbers, or scared to try. That is not enough. That is fear wearing calm clothing. Real enough is not anti ambition. It gives ambition a boundary.\n\nIn Nisk, a useful move is to write your enough point before life writes it for you. What level of runway would help you breathe? What lifestyle actually fits your values? What purchases are worth it? Which ones are just pressure? Then check that against your Dashboard and your simulations. Enough is not about picking a tiny life. It is about refusing to let comparison design your life for you.',
			flashcards: [
				{
					id: 'school-money-psychology-housel-03-fc-1',
					role: 'main_idea',
					front: 'What does enough protect?',
					back: 'It protects peace, freedom, and judgment from endless comparison.',
				},
				{
					id: 'school-money-psychology-housel-03-fc-2',
					role: 'watch_out',
					front: 'When does enough go wrong?',
					back: 'When it becomes fear, avoidance, or a reason not to grow.',
				},
				{
					id: 'school-money-psychology-housel-03-fc-3',
					role: 'use_it',
					front: 'How do you define enough?',
					back: 'Name what progress means before status pressure moves the target.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Define your enough point',
				prompt: 'Write what enough means for this season of your life.',
			},
			revision: 1,
		},
		{
			id: 'school-money-psychology-housel-04',
			title: 'Time is the quiet force',
			description:
				'This lesson teaches why money psychology respects patience, survival, and boring consistency.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['money-psychology-housel'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['money-psychology-housel', 'patience', 'consistency'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'time does heavy lifting',
				'patience under boredom',
				'survival matters',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Fast money gets attention. Slow money changes lives quietly. Money psychology notices that the biggest force in many money stories is not brilliance. It is time combined with behavior that survives.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes time rewards the person who can stay in the game. The hard part is not only finding a smart move. The hard part is not blowing up, quitting early, panicking, overspending, or needing every result to happen right now.',
				},
				{
					type: 'bullet_list',
					items: [
						'Time needs patience',
						'Time needs room for error',
						'Time needs behavior you can repeat',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is underestimating boring consistency because it does not feel impressive.',
				},
				{
					type: 'example',
					text: 'Someone who saves modestly for years may look less exciting than someone chasing big moves. But the steady person may build more freedom because they stayed consistent while the exciting person kept restarting.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that patience can be confused with passivity. Waiting is not magic by itself. Time helps when it is paired with useful behavior, learning, income growth, risk control, and regular review.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, connect this lens to a future scenario. Test what happens when a modest habit lasts for years instead of only feeling good for a week.',
				},
				{
					type: 'key_takeaway',
					text: 'Time helps the people who can keep showing up.',
				},
			],
			audioScript:
				'Money psychology respects time because time is quiet. It does not look dramatic in a short video. It does not feel impressive when you are just repeating a simple habit. It does not give you a quick identity boost. But over long stretches, time can become one of the strongest forces in your money life.\n\nThis lens says the big advantage is often not being the smartest person in the room. It is staying in the game long enough for good behavior to matter. That sounds boring, but it is not small. Many people do not lose because they never found the perfect tactic. They lose because they panic, restart, chase status, take too much risk, burn out, ignore their numbers, or quit before the slow part has time to work.\n\nThink about a modest habit. Maybe someone saves $200 or $300 a month. That may not feel life changing at first. It may even feel discouraging if they compare themselves to people online talking about huge income, huge portfolios, or dramatic wins. But if that habit survives year after year, it can become meaningful. Not because the person was a genius. Because they kept the behavior alive.\n\nMoney psychology is not saying small habits solve every problem. Income matters. Housing costs matter. Health matters. Family obligations matter. Structural pressure is real. But the lens is saying this: when you do have a useful behavior, time can multiply it. The problem is most people want the multiplication without the waiting. They want the result without the boring middle.\n\nThat boring middle is where temperament matters. Can you keep going when the result is not visible yet? Can you avoid destroying the plan because someone else looks ahead? Can you keep a little room for error so one emergency does not force you to quit? Can you accept that slow progress is still progress?\n\nThe common mistake is treating boring consistency as unimpressive. People underestimate it because it does not feel like a story. But a life with fewer restarts can beat a life full of intense bursts and collapses. The person who keeps a steady system through normal stress may end up with more freedom than the person who keeps chasing a dramatic fix.\n\nThe tradeoff is that patience can become passivity if you misuse it. Time does not fix a broken plan by itself. Waiting while avoiding your numbers is not patience. Staying in a bad pattern for ten years is not wisdom. Time helps when it is paired with behavior worth repeating. Saving, learning, increasing income, reducing fragile choices, reviewing your plan, and staying honest about what is working.\n\nInside Nisk, this is why simulation matters. A one month habit may look small. A five year habit can look different. A ten year habit can change the shape of your options. The point is not to worship patience. The point is to respect what happens when a useful behavior survives long enough to count.',
			flashcards: [
				{
					id: 'school-money-psychology-housel-04-fc-1',
					role: 'main_idea',
					front: 'Why does time matter?',
					back: 'Time can multiply useful behavior when you stay in the game.',
				},
				{
					id: 'school-money-psychology-housel-04-fc-2',
					role: 'watch_out',
					front: 'What is fake patience?',
					back: 'Waiting while avoiding numbers, risk, learning, or needed change.',
				},
				{
					id: 'school-money-psychology-housel-04-fc-3',
					role: 'use_it',
					front: 'How do you test time?',
					back: 'Simulate what a modest habit does when it lasts for years.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Test a steady habit',
			},
			revision: 1,
		},
		{
			id: 'school-money-psychology-housel-05',
			title: 'Where money psychology breaks down',
			description:
				'This lesson teaches the limits of the Housel style lens so humility does not become vague motivation.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['money-psychology-housel'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['money-psychology-housel', 'story-risk', 'action-gap'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['story risk', 'vague humility', 'action gap'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Money psychology is useful because it makes money human. But any useful lens can become a hiding place if you take it too far. This school can explain behavior so well that it forgets to demand action.',
				},
				{
					type: 'paragraph',
					text: 'The Housel style lens is strongest when it helps you understand risk, luck, enough, patience, and behavior. It gets weaker when every lesson becomes a nice story with no decision attached.',
				},
				{
					type: 'bullet_list',
					items: [
						'Stories can replace numbers',
						'Humility can become hesitation',
						'Understanding can become avoidance',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is feeling wiser without changing the system that keeps producing the same result.',
				},
				{
					type: 'example',
					text: 'Someone may say they understand their spending is emotional. That insight matters. But if they never review the pattern, set a boundary, or test a different plan, the insight becomes decoration.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens may underweight structure. Some users need rules, automation, accountability, income moves, or clear constraints more than another reflection. Psychology helps explain the pattern. It does not automatically rebuild the system.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use this lens honestly. Reflect, then test. Notice the behavior, then look at the Dashboard. Name the fear, then simulate the tradeoff. The point is better judgment, not endless self analysis.',
				},
				{
					type: 'key_takeaway',
					text: 'Insight only counts when it changes what you can repeat.',
				},
			],
			audioScript:
				'Money psychology is powerful because it tells the truth about humans. We are not clean spreadsheets. We are emotional, social, scared, hopeful, proud, tired, and shaped by our past. That is why this lens matters. But it also has a weakness. It can become so good at explaining behavior that it lets behavior stay the same.\n\nThis is where the lens breaks down. You can read about luck, risk, enough, patience, ego, and fear, then feel wiser without changing the system that keeps hurting you. The story feels meaningful. The insight feels calming. But the bank account, the spending pattern, the debt stress, the lack of runway, or the repeated impulse does not move.\n\nThat is the danger of story risk. A good story can teach. It can also replace action. Someone might say, I overspend because I grew up without much, and now spending makes me feel safe. That may be true. It may be an important insight. It deserves compassion. But if the insight never becomes a boundary, a review habit, a different environment, or a different plan, then it becomes decoration. It explains the loop without interrupting it.\n\nHumility can also go too far. Money psychology teaches that luck and risk matter. That is good. It keeps ego down and shame down. But if you take it too far, you may stop making decisions. You may think, everything is uncertain, so why try? That is not wisdom. That is paralysis. The point is not to control everything. The point is to make choices that can survive the fact that you do not control everything.\n\nAnother weakness is structure. Some people do not need another reflection prompt. They need a simple rule. They need automation. They need a spending boundary. They need to raise income. They need to stop checking status symbols for identity. They need to look at the numbers once a week. They need an environment that does not constantly poke the same weakness. Psychology explains why the pattern exists. Structure helps change what happens next.\n\nThis does not mean the Housel style lens is weak. It means it should be paired with action. The best version is not vague motivation. It is grounded humility. It says, my behavior matters, luck matters, risk matters, enough matters, and now I need to test what that means in my actual life.\n\nInside Nisk, that means reflection should lead somewhere. Open the Dashboard. Run a scenario. Check runway. Compare what you say you value against where the money goes. Do not use self understanding as a way to avoid the numbers. Use it as a way to face the numbers without shame.\n\nThe takeaway is direct: insight only counts when it changes what you can repeat. If a lesson makes you feel smarter but your system stays the same, keep going. The goal is not to collect wise stories. The goal is to build better judgment in real life.',
			flashcards: [
				{
					id: 'school-money-psychology-housel-05-fc-1',
					role: 'main_idea',
					front: 'Where can this lens fail?',
					back: 'It can explain behavior without changing the system.',
				},
				{
					id: 'school-money-psychology-housel-05-fc-2',
					role: 'watch_out',
					front: 'What is story risk?',
					back: 'Using a good explanation as a substitute for action.',
				},
				{
					id: 'school-money-psychology-housel-05-fc-3',
					role: 'use_it',
					front: 'How do you keep it grounded?',
					back: 'Reflect, then test the behavior against your real numbers.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Ground insight in your numbers',
			},
			revision: 1,
		},
	],
};
